import React, { useEffect, useState } from 'react';
import client from '../../shared/api/client';

interface AuthImageProps {
  path: string | null | undefined;
  alt: string;
  className?: string;
  onClick?: () => void;
}

/**
 * Loads a sensitive document through the authenticated /kyc-documents proxy
 * and renders it from an Object URL so the Authorization header is preserved.
 */
const AuthImage: React.FC<AuthImageProps> = ({ path, alt, className, onClick }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path) {
      setSrc(null);
      return;
    }

    let revoke: string | null = null;
    setError(false);
    setSrc(null);

    client
      .get(`/kyc-documents/${path}`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data as Blob);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setError(true));

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [path]);

  if (!path) {
    return (
      <div className={`${className} flex items-center justify-center bg-slate-800 text-slate-500 text-xs`}>
        Not provided
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center bg-red-500/10 text-red-400 text-xs`}>
        Failed to load
      </div>
    );
  }

  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center bg-slate-800`}>
        <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
      </div>
    );
  }

  const isPdf = path.toLowerCase().endsWith('.pdf');
  if (isPdf) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className={`${className} flex items-center justify-center bg-slate-800 text-indigo-400 text-sm hover:bg-slate-700 transition`}
      >
        Open PDF
      </a>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      style={onClick ? { cursor: 'zoom-in' } : undefined}
    />
  );
};

export default AuthImage;
